// 2026-09-24 - FormuLearning - Step 6 Model input — 약물/PK · 장 흡수 · 평형용해도 · 고급 설정 (이슈 #63)
// 파일 위치: frontend/src/features/step6/InputsSection.tsx
// 2026-11-03 수정 - 이슈 #114: viewer 는 문헌 검색·선택 입력·BA/흡수 방식 비활성(고급 설정 펼치기는 열어 둔다).
// 설계: 개정안_260924_Step6 PK 재설계 §3.2(데이터 항목)·§6(화면). 원형: 국민대 PK 아티팩트 Model input.

import { useState } from 'react'
import type { PkInputs, Step6View } from '../../api/step6'
import { searchPkParams, errorDetail } from '../../api/step6'
import { ADVANCED_FIELDS, classifyPapp, editedPaths, getPath, isEdited, reload, revert, setPath } from './pkModel'
import { GroupHead, NumField, Seg, inputCls } from './fields'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'

interface Props {
  projectId: string
  view: Step6View
  inputs: PkInputs
  onChange: (next: PkInputs) => void
}

const box = 'rounded border p-3'
const boxStyle = { borderColor: 'var(--border-subtle)', backgroundColor: 'var(--color-bg-panel)' }

export default function InputsSection({ projectId, view, inputs, onChange }: Props) {
  const [advOpen, setAdvOpen] = useState(false)
  const canWrite = useCanWrite()
  const [search, setSearch] = useState<{ busy: boolean; msg?: string; sources?: string[] }>({ busy: false })
  const set = (path: string) => (v: unknown) => onChange(setPath(inputs, path, v))
  const num = (path: string) => getPath(inputs, path) as number | null | undefined
  const groupProps = (group: string) => {
    const paths = view.link_groups[group] ?? []
    const edited = editedPaths(inputs, paths)
    const stale = view.stale.filter((p) => paths.includes(p))
    return {
      sources: paths.map((p) => view.linked[p]?.source).filter(Boolean) as string[],
      editedCount: edited.length,
      onRevert: () => onChange(revert(inputs, edited)),
      stale: stale.length > 0,
      onReload: () => onChange(reload(inputs, stale, view.linked)),
    }
  }

  const apiOptions = view.linked['drug.api_name']?.options ?? []
  const bio = inputs.bio
  const ab = inputs.absorption
  const cls = classifyPapp(ab.method, Number(ab.papp_cm_s))
  const av = inputs.advanced.area_volume_ratio_cm_inv
  const distal = inputs.advanced.distal_region_factor
  const advChanged = ADVANCED_FIELDS.filter((f) => inputs.advanced[f.key] !== f.def).length

  const runSearch = async () => {
    const name = inputs.drug.api_name
    if (!name) return
    setSearch({ busy: true })
    try {
      const r = await searchPkParams(projectId, name)
      if (!r.ok) {
        setSearch({ busy: false, msg: r.message })
        return
      }
      let next = inputs
      const applied: string[] = []
      if (r.half_life_h != null) { next = setPath(next, 'drug.half_life_h', r.half_life_h); applied.push(`t½ ${r.half_life_h} h`) }
      const vd = r.vd_l ?? (r.vd_l_per_kg != null ? r.vd_l_per_kg * (inputs.advanced.body_weight_kg ?? 70) : null)
      if (vd != null) { next = setPath(next, 'drug.vd_l', +vd.toPrecision(6)); applied.push(`Vd ${+vd.toPrecision(4)} L`) }
      if (r.bioavailability != null) { next = setPath(next, 'bio.F', r.bioavailability); applied.push(`F ${r.bioavailability}`) }
      if (r.fu != null) { next = setPath(next, 'bio.fu', r.fu); applied.push(`fu ${r.fu}`) }
      onChange(next)
      setSearch({ busy: false, msg: applied.length ? `문헌값 적용: ${applied.join(' · ')}` : '확립된 문헌값을 찾지 못했습니다. 직접 입력하세요.', sources: r.sources })
    } catch (e) {
      setSearch({ busy: false, msg: errorDetail(e) })
    }
  }

  return (
    <div className="mb-4">
      <div className="mb-2 flex items-baseline gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">Model input</h4>
        <span className="text-[10px] text-text-muted">이전 Step 값을 불러왔습니다. 수정한 칸은 노란색으로 표시됩니다.</span>
      </div>
      <div className="grid gap-3 lg:grid-cols-3">
        {/* ── Drug / PK ── */}
        <section className={box} style={boxStyle}>
          <GroupHead
            title="Drug / PK"
            {...groupProps('drug')}
            extra={
              <button type="button" onClick={runSearch} disabled={search.busy || !inputs.drug.api_name || !canWrite}
                className="rounded px-2 py-0.5 text-[10px] disabled:opacity-50"
                style={{ border: '1px solid var(--border-teal)', color: 'var(--color-accent-teal)' }}
                title={canWrite ? 'Vertex 로 t½·Vd·F·fu 문헌값을 찾아 채웁니다' : READ_ONLY_TITLE}>
                {search.busy ? '검색 중…' : '문헌 검색'}
              </button>
            }
          />
          {search.msg && (
            <div className="mb-2 rounded px-2 py-1 text-[10px] text-text-secondary" style={{ backgroundColor: 'var(--bg-chip-teal)' }}>
              {search.msg}
              {search.sources?.length ? <ul className="mt-0.5 list-disc pl-4">{search.sources.map((s, i) => <li key={i}>{s}</li>)}</ul> : null}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <label className="col-span-2 block">
              <span className="mb-0.5 block text-[10px] text-text-secondary">Active ingredient</span>
              <select value={inputs.drug.api_name ?? ''} onChange={(e) => set('drug.api_name')(e.target.value)} disabled={!canWrite} title={canWrite ? undefined : READ_ONLY_TITLE} className={inputCls}>
                {!inputs.drug.api_name && <option value="">—</option>}
                {apiOptions.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <NumField label="Dose" unit="mg" value={num('drug.dose_mg')} onCommit={set('drug.dose_mg')} edited={isEdited(inputs, 'drug.dose_mg')} />
            <NumField label="Half-life t½" unit="h" value={num('drug.half_life_h')} onCommit={set('drug.half_life_h')} edited={isEdited(inputs, 'drug.half_life_h')} />
            <NumField label="Volume of distribution Vd" unit="L" value={num('drug.vd_l')} onCommit={set('drug.vd_l')} edited={isEdited(inputs, 'drug.vd_l')} />
            <div className="block">
              <span className="mb-0.5 block text-[10px] text-text-secondary">Bioavailability (BA)</span>
              <Seg value={bio.mode} onChange={set('bio.mode')} disabled={!canWrite}
                options={[{ v: 'ref', label: 'Measured / Literature' }, { v: 'unk', label: 'Prediction' }]} />
            </div>
            {bio.mode === 'ref' ? (
              <NumField label="Measured or literature BA (F)" unit="0–1" value={bio.F} onCommit={set('bio.F')} edited={isEdited(inputs, 'bio.F')} />
            ) : (
              <>
                <NumField label="Hepatic blood flow Qh" value={bio.qh} onCommit={set('bio.qh')} />
                <NumField label="Fraction unbound fu" unit="0–1" value={bio.fu} onCommit={set('bio.fu')} edited={isEdited(inputs, 'bio.fu')} />
                <NumField label="Intrinsic clearance CLint" value={bio.clint} onCommit={set('bio.clint')} />
                <label className="block">
                  <span className="mb-0.5 block text-[10px] text-text-secondary">CLint basis</span>
                  <select value={bio.basis} onChange={(e) => set('bio.basis')(e.target.value)} disabled={!canWrite} title={canWrite ? undefined : READ_ONLY_TITLE} className={inputCls}>
                    <option value="whole">Liver plasma clearance</option>
                    <option value="hep">In vitro hepatocytes</option>
                  </select>
                </label>
                <p className="col-span-2 text-[10px] text-text-muted">Qh 와 CLint 는 같은 clearance/time 단위로 입력합니다. Hepatocyte 기반 CLint 는 자동 scaling 하지 않습니다.</p>
              </>
            )}
          </div>
        </section>

        {/* ── Intestinal absorption ── */}
        <section className={box} style={boxStyle}>
          <GroupHead title="Intestinal absorption" {...groupProps('absorption')} />
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <Seg value={ab.method} onChange={set('absorption.method')} disabled={!canWrite}
                options={[{ v: 'Caco-2', label: 'Caco-2' }, { v: 'PAMPA', label: 'PAMPA' }]} />
            </div>
            <div className="col-span-2">
              <NumField label={`${ab.method} permeability`} unit="cm/s" mono value={ab.papp_cm_s} onCommit={set('absorption.papp_cm_s')} edited={isEdited(inputs, 'absorption.papp_cm_s')} />
            </div>
            <NumField label="Upper intestine transit" unit="h" value={ab.upper_h} onCommit={set('absorption.upper_h')} />
            <NumField label="Lower intestine transit" unit="h" value={ab.lower_h} onCommit={set('absorption.lower_h')} />
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] text-text-secondary">
            {cls ? (
              <>
                <span className="rounded-full px-2 py-0.5" style={{ backgroundColor: 'var(--bg-chip-purple)', color: 'var(--color-accent-purple)' }}>{cls[0]} permeability</span>
                <span>Peff <b className="font-mono">{cls[1].toExponential(2)}</b> cm/s</span>
                <span className="text-text-muted">ka 상부 {(cls[1] * av * 3600).toFixed(3)} · 하부 {(cls[1] * distal * av * 3600).toFixed(3)} h⁻¹</span>
              </>
            ) : <span style={{ color: 'var(--color-warn)' }}>Papp 값을 확인하세요</span>}
          </div>
        </section>

        {/* ── Equilibrium solubility ── */}
        <section className={box} style={boxStyle}>
          <GroupHead title="Equilibrium solubility" {...groupProps('solubility')} />
          <div className="grid grid-cols-3 gap-1.5">
            {inputs.solubility.map((r, i) => (
              <NumField key={r.ph} label={`pH ${r.ph.toFixed(1)}`} unit="mg/mL" align="center" value={r.mg_ml}
                edited={isEdited(inputs, 'solubility')}
                onCommit={(v) => onChange(setPath(inputs, 'solubility', inputs.solubility.map((x, k) => (k === i ? { ...x, mg_ml: v } : x))))} />
            ))}
          </div>
          <p className="mt-1 text-[10px] text-text-muted">Step 2 곡선은 Henderson–Hasselbalch 이론값입니다(염 상한 미반영). 실측값이 있으면 덮어쓰세요.</p>
        </section>
      </div>

      {/* ── 고급 설정 ── */}
      <div className="mt-2">
        <button type="button" onClick={() => setAdvOpen((o) => !o)} className="text-[10px] text-text-secondary hover:text-text-body">
          {advOpen ? '▾' : '▸'} 고급 설정 (위장관 생리 · 투과 · 석출/재용해 · 계산설정)
          {advChanged > 0 && <span style={{ color: 'var(--color-warn)' }}> · 기본값에서 {advChanged}개 변경</span>}
        </button>
        {advOpen && (
          <div className="mt-2 grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
            {ADVANCED_FIELDS.map((f) => (
              <NumField key={f.key} label={f.label} unit={f.unit} value={inputs.advanced[f.key]} title={`${f.group} · 기본값 ${f.def}`}
                edited={inputs.advanced[f.key] !== f.def} onCommit={(v) => set(`advanced.${f.key}`)(v ?? f.def)} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
